import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

interface ProductQuestion {
  id: number;
  product_name: string;
  customer_name: string;
  customer_email: string;
  question: string;
  answer: string | null;
  status: 'pending' | 'answered';
  created_at: string;
  answered_at: string | null;
}

const ProductQuestions: React.FC = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<ProductQuestion[]>([]);
  const [allQuestions, setAllQuestions] = useState<ProductQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'answered'>('all');
  const [selectedQuestion, setSelectedQuestion] = useState<ProductQuestion | null>(null);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    // Filter'a göre soruları filtrele
    if (filter === 'all') {
      setQuestions(allQuestions);
    } else {
      setQuestions(allQuestions.filter(q => q.status === filter));
    }
  }, [filter, allQuestions]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const sessionToken = localStorage.getItem('admin_session_token');
      
      if (!sessionToken) {
        navigate('/admin');
        return;
      }

      const response = await fetch(`${API_URL}/admin/product-questions?status=all`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setAllQuestions(data.questions);
        setQuestions(data.questions);
      }
    } catch (error) {
      // Hata sessizce yoksayılır
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSubmit = async (questionId: number) => {
    if (!answer.trim()) {
      alert('Lütfen bir cevap yazın');
      return;
    }

    try {
      setSubmitting(true);
      const sessionToken = localStorage.getItem('admin_session_token');

      const response = await fetch(`${API_URL}/admin/product-questions/${questionId}/answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({ answer })
      });

      const data = await response.json();

      if (data.success) {
        alert('Cevap başarıyla gönderildi!');
        setAnswer('');
        setSelectedQuestion(null);
        fetchQuestions();
      } else {
        alert(data.error || 'Cevap gönderilemedi');
      }
    } catch (error) {
      alert('Cevap gönderilirken bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR');
  };

  return (
    <PageContainer>
      <Helmet>
        <title>Ürün Soruları - Admin Panel</title>
      </Helmet>

      <Container>
        <Header>
          <Title>Ürün Soruları</Title>
          <BackButton onClick={() => navigate('/admin')}>
            ← Admin Panele Dön
          </BackButton>
        </Header>

        <FiltersSection>
          <FilterButton 
            active={filter === 'all'} 
            onClick={() => setFilter('all')}
          >
            Tümü ({allQuestions.length})
          </FilterButton>
          <FilterButton 
            active={filter === 'pending'} 
            onClick={() => setFilter('pending')}
          >
            Bekleyenler ({allQuestions.filter(q => q.status === 'pending').length})
          </FilterButton>
          <FilterButton 
            active={filter === 'answered'} 
            onClick={() => setFilter('answered')}
          >
            Cevaplananlar ({allQuestions.filter(q => q.status === 'answered').length})
          </FilterButton>
        </FiltersSection>

        {loading ? (
          <LoadingMessage>Yükleniyor...</LoadingMessage>
        ) : questions.length === 0 ? (
          <EmptyMessage>Henüz soru bulunmuyor</EmptyMessage>
        ) : (
          <QuestionsGrid>
            {questions.map((question) => (
              <QuestionCard key={question.id}>
                <QuestionHeader>
                  <ProductName>{question.product_name}</ProductName>
                  <StatusBadge status={question.status}>
                    {question.status === 'pending' ? 'Bekliyor' : 'Cevaplandı'}
                  </StatusBadge>
                </QuestionHeader>

                <CustomerInfo>
                  <InfoRow>
                    <Label>Müşteri:</Label>
                    <Value>{question.customer_name}</Value>
                  </InfoRow>
                  <InfoRow>
                    <Label>Email:</Label>
                    <Value>{question.customer_email}</Value>
                  </InfoRow>
                  <InfoRow>
                    <Label>Tarih:</Label>
                    <Value>{formatDate(question.created_at)}</Value>
                  </InfoRow>
                </CustomerInfo>

                <QuestionSection>
                  <SectionTitle>Soru:</SectionTitle>
                  <QuestionText>{question.question}</QuestionText>
                </QuestionSection>

                {question.answer && (
                  <AnswerSection>
                    <SectionTitle>Cevap:</SectionTitle>
                    <AnswerText>{question.answer}</AnswerText>
                    <AnsweredDate>
                      Cevaplandı: {question.answered_at && formatDate(question.answered_at)}
                    </AnsweredDate>
                  </AnswerSection>
                )}

                {question.status === 'pending' && (
                  <>
                    {selectedQuestion?.id === question.id ? (
                      <AnswerForm>
                        <Textarea
                          value={answer}
                          onChange={(e) => setAnswer(e.target.value)}
                          placeholder="Cevabınızı yazın..."
                          rows={5}
                        />
                        <ButtonGroup>
                          <SubmitButton 
                            onClick={() => handleAnswerSubmit(question.id)}
                            disabled={submitting}
                          >
                            {submitting ? 'Gönderiliyor...' : 'Cevabı Gönder'}
                          </SubmitButton>
                          <CancelButton onClick={() => {
                            setSelectedQuestion(null);
                            setAnswer('');
                          }}>
                            İptal
                          </CancelButton>
                        </ButtonGroup>
                      </AnswerForm>
                    ) : (
                      <AnswerButton onClick={() => setSelectedQuestion(question)}>
                        Cevapla
                      </AnswerButton>
                    )}
                  </>
                )}
              </QuestionCard>
            ))}
          </QuestionsGrid>
        )}
      </Container>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: #0d0d0d;
  padding: 2rem 0;
  overflow-y: auto;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  padding-bottom: 4rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #d4af37, #f4e4bc);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const BackButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: transparent;
  border: 1px solid #d4af37;
  border-radius: 8px;
  color: #d4af37;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(212, 175, 55, 0.1);
  }
`;

const FiltersSection = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  background: ${props => props.active ? '#d4af37' : 'transparent'};
  border: 1px solid #d4af37;
  border-radius: 8px;
  color: ${props => props.active ? '#0d0d0d' : '#d4af37'};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.active ? '#b8941f' : 'rgba(212, 175, 55, 0.1)'};
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #cfcfcf;
  font-size: 1.125rem;
  padding: 3rem;
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: #cfcfcf;
  font-size: 1.125rem;
  padding: 3rem;
  background: #1a1a1a;
  border-radius: 12px;
  border: 1px solid #333333;
`;

const QuestionsGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const QuestionCard = styled(motion.div)`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid #333333;
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #333333;
`;

const ProductName = styled.h3`
  color: #ffffff;
  font-size: 1.25rem;
  font-weight: 600;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  background: ${props => props.status === 'pending' ? '#ff9800' : '#4caf50'};
  color: #ffffff;
`;

const CustomerInfo = styled.div`
  margin-bottom: 1.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const Label = styled.span`
  color: #888888;
  font-size: 0.875rem;
  min-width: 80px;
`;

const Value = styled.span`
  color: #cfcfcf;
  font-size: 0.875rem;
`;

const QuestionSection = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(212, 175, 55, 0.05);
  border-left: 4px solid #d4af37;
  border-radius: 4px;
`;

const AnswerSection = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(76, 175, 80, 0.05);
  border-left: 4px solid #4caf50;
  border-radius: 4px;
`;

const SectionTitle = styled.div`
  color: #d4af37;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
`;

const QuestionText = styled.p`
  color: #ffffff;
  font-size: 1rem;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const AnswerText = styled.p`
  color: #ffffff;
  font-size: 1rem;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const AnsweredDate = styled.div`
  color: #888888;
  font-size: 0.75rem;
  margin-top: 0.5rem;
`;

const AnswerForm = styled.div`
  margin-top: 1.5rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 1rem;
  background: #0d0d0d;
  border: 1px solid #333333;
  border-radius: 8px;
  color: #ffffff;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 1rem;

  &:focus {
    outline: none;
    border-color: #d4af37;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
`;

const SubmitButton = styled.button`
  padding: 0.75rem 2rem;
  background: #4caf50;
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: #45a049;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 0.75rem 2rem;
  background: transparent;
  border: 1px solid #888888;
  border-radius: 8px;
  color: #888888;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(136, 136, 136, 0.1);
  }
`;

const AnswerButton = styled.button`
  padding: 0.75rem 2rem;
  background: #d4af37;
  border: none;
  border-radius: 8px;
  color: #0d0d0d;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #b8941f;
  }
`;

export default ProductQuestions;
